<template>
	<article class="d-login-page">
		<SvgLoginBackground
			class="
				o-svg-themify
				absolute
				left-0
				top-0
				w-full
				h-full
				max-w-visual-screen max-h-visual-screen
			"
		/>
		<header class="relative w-full >=768:px-1/12col >=1024:px-2/12col">
			<!-- Vive logo -->
			<TransitionExt
				v-if="$store.state.site.url"
				name="fade-in-out"
				duration="400"
			>
				<NuxtLinkExt
					:to="$store.state.site.url"
					aria-label="VIVE - Det nationale forsknings- og analysecenter for velfærd"
					class="inline-flex gap-x-xs/h pointer-events-auto"
				>
					<SvgViveLogo class="w-site-header-logo" />

					<TransitionExt name="fade-in-out" duration="200">
						<SvgViveSubLogoEn
							v-if="lang === 'en'"
							class="w-site-header-sub-logo-en <768:hidden"
						/>
						<SvgViveSubLogo
							v-else
							class="w-site-header-sub-logo <768:hidden"
						/>
					</TransitionExt>
				</NuxtLinkExt>
			</TransitionExt>
			<h1
				v-if="intro && intro.title"
				class="sr-only"
				v-html="intro.title"
			></h1>
		</header>

		<div class="w-full >=768:px-1/12col >=1024:px-2/12col my-auto">
			<template v-if="isLoggedIn">
				<p class="block">Du er logget ind.</p>

				<BaseButton
					class="
						mt-lg/v
						px-xs/v
						text-white
						border border-white
						bg-transparent
						hover:bg-white hover:bg-opacity-10
						pointer-events-auto
					"
					:aria-disabled="isLoggingOut ? 'true' : null"
					@click="logOut"
				>
					Log ud
				</BaseButton>
			</template>
			<template v-else>
				<BaseH2
					v-if="login.title"
					:id="`d-login-page-${$vnode.key}__title`"
					class="text-teaser"
					v-html="login.title"
				/>
				<AxiosForm
					v-if="login.endpointUrl"
					method="post"
					:action="login.endpointUrl"
					:aria-labelledby="
						login.title ? `d-login-page-${$vnode.key}__title` : null
					"
					class="
						w-full
						mt-80
						>=1024:mt-64
						flex flex-col
						gap-y-18
						transition-opacity
						duration-200
					"
					:class="{
						'opacity-50': loading,
					}"
					@submit="onFormSubmit"
					@complete="onFormComplete"
					@error="onFormError"
				>
					<template v-for="(item, index) in login.fields">
						<FormTextInput
							v-if="
								[
									'text',
									'email',
									'number',
									'password',
								].includes(item.type)
							"
							:key="index"
							v-bind="{ ...item, ...getSpecificBindings(item) }"
							v-model="item.value"
						/>

						<FormDropdown
							v-if="['dropdown'].includes(item.type)"
							:key="index"
							v-bind="{ ...item, ...getSpecificBindings(item) }"
							v-model="item.value"
						/>

						<div
							v-if="['button', 'submit'].includes(item.type)"
							:key="index"
							class="
								mt-lg/v
								flex flex-col
								>=605:flex-row >=605:items-baseline
								gap-x-2xs/h gap-y-2xs/v
							"
						>
							<div
								v-if="message && !message.success"
								role="alert"
								class="text-input-form italic"
								tabindex="-1"
							>
								{{ message.message }}
							</div>

							<BaseButton
								tag="input"
								class="
									>=605:w-auto
									>=605:justify-self-end
									>=605:ml-auto
									bg-white
									hover:bg-theme-10
									text-theme
								"
								v-bind="{
									...item,
									...getSpecificBindings(item),
								}"
							/>
						</div>
					</template>
				</AxiosForm>
			</template>
		</div>
	</article>
</template>

<script>
import { mapGetters } from 'vuex';

import AxiosForm from '~/components/shared/AxiosForm';
import FormTextInput from '~/components/main/FormTextInput';
import FormDropdown from '~/components/main/FormDropdown';
import SvgLoginBackground from '~/assets/svgs/login-background.svg?inline';
import SvgViveLogo from '~/assets/svgs/vive-logo.svg?inline';
import SvgViveSubLogo from '~/assets/svgs/vive-sub-logo.svg?inline';
import SvgViveSubLogoEn from '~/assets/svgs/vive-sub-logo-en.svg?inline';

export default {
	name: 'MainOverviewList',

	components: {
		AxiosForm,
		FormTextInput,
		FormDropdown,
		SvgLoginBackground,
		SvgViveLogo,
		SvgViveSubLogo,
		SvgViveSubLogoEn,
	},
	inheritAttrs: false,

	props: {
		intro: Object,
		login: Object,
	},

	data() {
		const { returnUrl = this.$store.state.site?.url ?? '/' } =
			this.$route.query;
		const lang =
			{
				11050: 'en',
			}[this.$store.state?.site?.cultureId] || 'da';

		return {
			lang,
			message: null,
			loading: false,
			isLoggingOut: false,
			returnUrl,
		};
	},

	computed: {
		...mapGetters(['isLoggedIn']),
	},

	watch: {
		message() {
			this.$nextTick(() => {
				const focusEl = this.$el.querySelector(
					'[role="status"], [role="alert"]'
				);
				focusEl?.focus?.();
			});
		},
	},

	methods: {
		onFormSubmit() {
			this.message = null;
			this.loading = true;
		},

		onFormComplete({ data }) {
			this.loading = false;
			this.$store.commit('SET_IS_LOGGED_IN', !!data);
			this.$router.replace(this.returnUrl);
		},

		onFormError({ response }) {
			this.loading = false;
			this.message = {
				culture: 'da-DK',
				message: response.data || 'Noget gik galt. Prøv igen',
				statusCode: response.status,
				success: false,
			};
		},

		getSpecificBindings(field) {
			const bindings = {};

			// Text inputs
			if (['email'].includes(field.name)) {
				bindings.type = 'email';
			}
			if (['zipcode'].includes(field.name)) {
				bindings.type = 'number';
				bindings.min = 1000;
				bindings.max = 9990;
			}

			// Button inputs
			if (['button', 'submit'].includes(field.type)) {
				bindings.type = 'submit';
				bindings.label = null;
				bindings.value = field.label;
			}
			return bindings;
		},

		logOut() {
			this.isLoggingOut = true;
			this.$axios
				.get('/api/login/logout')
				.then(({ data }) => {
					if (!data?.loggedIn) {
						this.$store.commit('SET_IS_LOGGED_IN', false);
					}
					this.isLoggingOut = false;
					this.$nuxt.refresh();
				})
				.catch(() => {
					this.isLoggingOut = false;
				});
		},
	},
};
</script>

<style lang="postcss">
.d-login-page {
	@apply text-white flex flex-col px-layout-margin py-xl/v;

	& > svg {
		z-index: -1;
	}
}
.d-login-page:before {
	@apply bg-theme;
	content: '';
	display: block;
	position: fixed;
	left: -50vw; /* for some reason blur triggers weird stuff if just 0 0 and 100vw and vh */
	top: 0;
	width: 200vw;
	height: calc(
		100vh + 9999999px
	); /* and for some reason the blur cuts the bottom of the page if just 100vh / treats it as semi-absolute? */
	z-index: -1;
}
</style>
